/**
 * deskAccent — a stable per-desk accent color derived from the desk id.
 *
 * The Triptych gives every desk a colored spine, focus ring, and dot
 * (`--dc` in the CSS). The near-term design does NOT add a `color` field to
 * the desk API, so we derive a deterministic accent from the desk id over a
 * curated palette (the agent-identity hues already in tokens.css). Same id
 * always yields the same color; the agent-activity hue (`--accent-active`)
 * AND the incident red (`--incident` / `--agent-ops`) are intentionally
 * excluded so a resting desk accent never collides with "agent activity" or
 * reads as an incident (house rule: red exists only as the INCIDENT stamp).
 *
 * Returns a hex string to set as the inline `--dc` custom property:
 *   <div style={{ "--dc": deskAccent(deskId) } as CSSProperties}>
 */

// Curated from the --agent-* identity tokens in styles/tokens.css (the Glass
// & Print agent-identity ramp). Ordered so adjacent desks tend to land on
// visually distinct hues.
//
// We DERIVE accents by hashing the real desk id, NOT by desk name, so accents
// stay stable as desks are renamed and no `color` field is added to the wire.
// There is intentionally NO name→colour runtime branch here; renaming the
// slots or special-casing names would shift every other desk's hue.
//
// --agent-ops (#b4452f) is excluded: it equals the INCIDENT red, and a resting
// desk accent must never read as an incident. --agent-design duplicates
// --agent-gtm, so it is folded into one slot.
const PALETTE = [
  "#8a6a28", // --agent-pm      warm bronze
  "#2e6b45", // --agent-dev     quiet green
  "#3b66c4", // --agent-analytics blue
  "#6d4fa3", // --agent-comms   violet
  "#2c6f8f", // --agent-qa      sky
  "#a3488f", // --agent-gtm     magenta
  "#46519e", // --agent-finance indigo
  "#2e7f6b", // --agent-support sea green
] as const;

/** Deterministic 32-bit string hash (FNV-1a), stable across runs/processes. */
function hashString(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // hash *= 16777619, kept in 32-bit unsigned space
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

export function deskAccent(deskId: string): string {
  if (!deskId) return PALETTE[0]!;
  return PALETTE[hashString(deskId) % PALETTE.length]!;
}

/** Exposed for tests / consumers that want the full set. */
export const DESK_ACCENT_PALETTE = PALETTE;

/** The same slots as tokens, so a scheme switch re-tints them (ADR-0045). */
const TOKEN_PALETTE = [
  "var(--agent-pm)",
  "var(--agent-dev)",
  "var(--agent-analytics)",
  "var(--agent-comms)",
  "var(--agent-qa)",
  "var(--agent-gtm)",
  "var(--agent-finance)",
  "var(--agent-support)",
] as const;

/**
 * The Team's colour as a token, for the rail's Team mark and anywhere else the
 * colour must follow the scheme. Same hash and slot as {@link deskAccent}, so
 * the two never disagree about a Team's hue.
 */
export function deskAccentToken(deskId: string): string {
  if (!deskId) return TOKEN_PALETTE[0]!;
  return TOKEN_PALETTE[hashString(deskId) % TOKEN_PALETTE.length]!;
}
